// Microsoft Fabric Architecture Templates
import defaultParamsByType from '../defaultParamsByType';

export type TemplateLevel = 'Starter' | 'Intermédiaire' | 'Production' | 'Enterprise';

export interface FabricTemplate {
  id: string;
  name: string;
  description: string;
  level: TemplateLevel;
  icon: string;
  nodes: any[];
  edges: any[];
}

const ICONS = {
  capacity:          '/icons/fabric/fabric_capacity.svg',
  workspace:         '/icons/fabric/group_workspace_64_non-item.svg',
  domain:            '/icons/azure/10007-icon-service-Resource-Groups.svg',
  lakehouse:         '/icons/fabric/lakehouse_64_item.svg',
  pipeline:          '/icons/fabric/pipeline_64_item.svg',
  notebook:          '/icons/fabric/notebook_64_item.svg',
  semantic_model:    '/icons/fabric/semantic_model_64_item.svg',
  report:            '/icons/fabric/power_bi_48_color.svg',
  warehouse:         '/icons/fabric/data_warehouse_64_item.svg',
  dataflow:          '/icons/fabric/dataflow_gen2_64_item.svg',
  eventstream:       '/icons/fabric/eventstream_64_item.svg',
  eventhouse:        '/icons/fabric/event_house_64_item.svg',
  kql_database:      '/icons/fabric/kql_database_64_item.svg',
  ml_model:          '/icons/fabric/data_science_48_color.svg',
  data_agent:        '/icons/fabric/data_agent_64_item.svg',
  deployment_pipeline: '/icons/fabric/copy_job_64_item.svg',
};

function cap(id: string, label: string, sku: string, x: number, y: number, w: number, h: number) {
  const defaults = (defaultParamsByType as any)['fabric_capacity'] || {};
  return {
    id, type: 'group', position: { x, y }, width: w, height: h,
    style: { backgroundColor: '#E0F7FA' },
    data: {
      type: 'fabric_capacity', resourceType: 'fabric_capacity',
      label, name: label, icon: ICONS.capacity, color: '#E0F7FA',
      params: {
        ...defaults,
        display_name: label.toLowerCase().replace(/\s+/g, '-'),
        name: label.toLowerCase().replace(/\s+/g, '-'),
        sku,
        location: 'West Europe',
      },
      properties: {}, config: {},
    },
  };
}

function ws(id: string, label: string, parentId: string, x: number, y: number, w: number, h: number) {
  const defaults = (defaultParamsByType as any)['fabric_workspace'] || {};
  return {
    id, type: 'group', position: { x, y }, width: w, height: h,
    parentNode: parentId, extent: 'parent', expandParent: true,
    style: { backgroundColor: '#E8F5E9' },
    data: {
      type: 'fabric_workspace', resourceType: 'fabric_workspace',
      label, name: label, icon: ICONS.workspace, color: '#E8F5E9',
      params: {
        ...defaults,
        display_name: label.toLowerCase().replace(/\s+/g, '-'),
        name: label.toLowerCase().replace(/\s+/g, '-'),
        capacityId: parentId,
      },
      properties: {}, config: {},
    },
  };
}

function item(id: string, type: string, label: string, parentId: string, x: number, y: number) {
  const iconKey = type.replace('fabric_', '') as keyof typeof ICONS;
  const defaults = (defaultParamsByType as any)[type] || {};
  return {
    id, type: 'custom', position: { x, y },
    parentNode: parentId, extent: 'parent', expandParent: true,
    data: {
      type, resourceType: type,
      label, name: label, icon: ICONS[iconKey] || ICONS.lakehouse,
      editing: false,
      params: {
        ...defaults,
        display_name: label.toLowerCase().replace(/\s+/g, '-'),
        name: label.toLowerCase().replace(/\s+/g, '-'),
        workspaceId: parentId,
      },
      properties: {}, config: {},
    },
  };
}

function dom(id: string, label: string, parentId: string, x: number, y: number, w: number, h: number) {
  const defaults = (defaultParamsByType as any)['fabric_domain'] || {};
  return {
    id, type: 'group', position: { x, y }, width: w, height: h,
    parentNode: parentId, extent: 'parent', expandParent: true,
    style: { backgroundColor: '#F3E8FF' },
    data: {
      type: 'fabric_domain', resourceType: 'fabric_domain',
      label, name: label, icon: ICONS.domain, color: '#F3E8FF',
      params: {
        ...defaults,
        name: label.toLowerCase().replace(/\s+/g, '-'),
        display_name: label,
      },
      properties: {}, config: {},
    },
  };
}

// workspace dans un domain (parentNode = domainId, capacityId dans params)
function wsd(id: string, label: string, domainId: string, capacityId: string, x: number, y: number, w: number, h: number) {
  const defaults = (defaultParamsByType as any)['fabric_workspace'] || {};
  return {
    id, type: 'group', position: { x, y }, width: w, height: h,
    parentNode: domainId, extent: 'parent', expandParent: true,
    style: { backgroundColor: '#E8F5E9' },
    data: {
      type: 'fabric_workspace', resourceType: 'fabric_workspace',
      label, name: label, icon: ICONS.workspace, color: '#E8F5E9',
      params: {
        ...defaults,
        display_name: label.toLowerCase().replace(/\s+/g, '-'),
        name: label.toLowerCase().replace(/\s+/g, '-'),
        capacityId,
      },
      properties: {}, config: {},
    },
  };
}

function edge(id: string, source: string, target: string) {
  return { id, source, target };
}

export const fabricTemplates: FabricTemplate[] = [

  // ── STARTER ──────────────────────────────────────────────────────────────

  {
    id: 'fabric-starter',
    name: 'Starter / Démo',
    description: '1 capacity F2 · 1 workspace · pipeline + lakehouse + notebook + semantic model + rapport. Idéal pour POC, démonstrations et premiers projets Fabric.',
    level: 'Starter',
    icon: '🚀',
    nodes: [
      cap('cap', 'Fabric Capacity (F2)', 'F2', 0, 0, 560, 420),
      ws('ws', 'Workspace Demo', 'cap', 30, 60, 500, 320),
      item('lh',  'fabric_lakehouse',      'Lakehouse',        'ws',  30, 100),
      item('pl',  'fabric_pipeline',       'Pipeline',         'ws', 210, 100),
      item('nb',  'fabric_notebook',       'Notebook',         'ws', 390, 100),
      item('sm',  'fabric_semantic_model', 'Semantic Model',   'ws',  30, 230),
      item('rpt', 'fabric_report',         'Power BI Report',  'ws', 210, 230),
    ],
    edges: [
      edge('e1', 'pl', 'lh'),
      edge('e2', 'lh', 'nb'),
      edge('e3', 'nb', 'sm'),
      edge('e4', 'sm', 'rpt'),
    ],
  },

  {
    id: 'fabric-bi-only',
    name: 'BI Reporting Only',
    description: '1 capacity F2 · workspace reporting · lakehouse Gold + datamart + semantic models + rapports. Pour les équipes BI qui ont déjà leurs pipelines de données.',
    level: 'Starter',
    icon: '📊',
    nodes: [
      cap('cap', 'Fabric Capacity (F2)', 'F2', 0, 0, 560, 400),
      ws('ws', 'Workspace Reporting', 'cap', 30, 60, 500, 300),
      item('lh',  'fabric_lakehouse',      'Lakehouse Gold',           'ws',  30, 90),
      item('wh',  'fabric_warehouse',      'Datamart',                 'ws', 210, 90),
      item('sm1', 'fabric_semantic_model', 'Semantic Model (DirectLake)', 'ws', 30, 220),
      item('sm2', 'fabric_semantic_model', 'Semantic Model (Import)',  'ws', 210, 220),
      item('rpt', 'fabric_report',         'Power BI Report',          'ws', 390, 150),
    ],
    edges: [
      edge('e1', 'lh',  'sm1'),
      edge('e2', 'wh',  'sm2'),
      edge('e3', 'sm1', 'rpt'),
      edge('e4', 'sm2', 'rpt'),
    ],
  },

  // ── INTERMÉDIAIRE ─────────────────────────────────────────────────────────

  {
    id: 'fabric-medallion',
    name: 'Medallion Architecture',
    description: '1 capacity F8 · 3 workspaces Bronze / Silver / Gold. Pattern de référence pour structurer les données par qualité avec gouvernance et sécurité par couche.',
    level: 'Intermédiaire',
    icon: '🏅',
    nodes: [
      cap('cap', 'Fabric Capacity (F8)', 'F8', 0, 0, 900, 440),

      ws('ws-b', 'Workspace Bronze',  'cap',  30, 65, 250, 335),
      item('lh-b', 'fabric_lakehouse', 'Lakehouse Bronze', 'ws-b',  30, 90),
      item('pl-b', 'fabric_pipeline',  'Pipeline Ingestion', 'ws-b', 30, 220),
      item('es',   'fabric_eventstream','EventStream',       'ws-b', 170, 90),

      ws('ws-s', 'Workspace Silver', 'cap', 320, 65, 240, 335),
      item('lh-s', 'fabric_lakehouse', 'Lakehouse Silver',   'ws-s',  30, 90),
      item('df',   'fabric_dataflow',  'Dataflow Transform', 'ws-s',  30, 220),
      item('nb',   'fabric_notebook',  'Notebook ETL',       'ws-s', 160, 220),

      ws('ws-g', 'Workspace Gold + BI', 'cap', 600, 65, 270, 335),
      item('lh-g', 'fabric_lakehouse',      'Lakehouse Gold', 'ws-g',  30, 90),
      item('sm',   'fabric_semantic_model', 'Semantic Model', 'ws-g',  30, 220),
      item('rpt',  'fabric_report',         'Power BI Report','ws-g', 170, 220),
    ],
    edges: [
      edge('e1', 'pl-b', 'lh-b'),
      edge('e2', 'es',   'lh-b'),
      edge('e3', 'lh-b', 'lh-s'),
      edge('e4', 'df',   'lh-s'),
      edge('e5', 'lh-s', 'lh-g'),
      edge('e6', 'lh-g', 'sm'),
      edge('e7', 'sm',   'rpt'),
    ],
  },

  {
    id: 'fabric-realtime',
    name: 'Real-Time Intelligence',
    description: '1 capacity F8 · pipeline temps réel EventStream → Eventhouse → KQL Database → Dashboard. Idéal pour IoT, logs applicatifs, clickstream et monitoring opérationnel.',
    level: 'Intermédiaire',
    icon: '⚡',
    nodes: [
      cap('cap', 'Fabric Capacity (F8)', 'F8', 0, 0, 600, 400),
      ws('ws', 'Workspace Real-Time Intelligence', 'cap', 30, 60, 540, 300),
      item('es',  'fabric_eventstream',  'EventStream',      'ws',  30, 100),
      item('eh',  'fabric_eventhouse',   'Eventhouse',       'ws', 200, 100),
      item('kql', 'fabric_kql_database', 'KQL Database',     'ws', 370, 100),
      item('sm',  'fabric_semantic_model','Semantic Model',  'ws', 200, 220),
      item('rpt', 'fabric_report',       'Real-Time Report', 'ws', 370, 220),
    ],
    edges: [
      edge('e1', 'es',  'eh'),
      edge('e2', 'eh',  'kql'),
      edge('e3', 'kql', 'sm'),
      edge('e4', 'sm',  'rpt'),
    ],
  },

  {
    id: 'fabric-datascience',
    name: 'Data Science & AI',
    description: '1 capacity F16 · workspace dédié ML et IA : feature store, notebooks d\'entraînement, modèles ML et agents IA Copilot sur données d\'entreprise.',
    level: 'Intermédiaire',
    icon: '🤖',
    nodes: [
      cap('cap', 'Fabric Capacity (F16)', 'F16', 0, 0, 600, 400),
      ws('ws', 'Workspace Data Science & AI', 'cap', 30, 60, 540, 300),
      item('lh',  'fabric_lakehouse',      'Feature Store',        'ws',  30, 100),
      item('nb1', 'fabric_notebook',       'Notebook Training',    'ws', 200, 100),
      item('ml',  'fabric_ml_model',       'ML Model',             'ws', 370, 100),
      item('nb2', 'fabric_notebook',       'Notebook Scoring',     'ws', 200, 220),
      item('da',  'fabric_data_agent',     'Data Agent (Copilot)', 'ws', 370, 220),
    ],
    edges: [
      edge('e1', 'lh',  'nb1'),
      edge('e2', 'nb1', 'ml'),
      edge('e3', 'ml',  'nb2'),
      edge('e4', 'lh',  'da'),
      edge('e5', 'nb2', 'da'),
    ],
  },

  // ── PRODUCTION ────────────────────────────────────────────────────────────

  {
    id: 'fabric-production',
    name: 'Fabric Production',
    description: '2 capacités séparées : Ingestion F16 (Bronze + Silver) et Analytics F32 (Gold + Reporting). Évite que les pipelines lourds impactent les rapports Power BI interactifs.',
    level: 'Production',
    icon: '🏭',
    nodes: [
      cap('cap-a', 'Capacity Ingestion (F16)', 'F16',   0, 0, 580, 440),
      ws('ws-b', 'Workspace Bronze', 'cap-a',  30, 65, 250, 335),
      item('lh-b', 'fabric_lakehouse', 'Lakehouse Bronze',   'ws-b',  30, 90),
      item('pl',   'fabric_pipeline',  'Pipeline Ingestion', 'ws-b',  30, 220),
      item('es',   'fabric_eventstream','EventStream',       'ws-b', 170, 90),
      ws('ws-s', 'Workspace Silver', 'cap-a', 320, 65, 230, 335),
      item('lh-s', 'fabric_lakehouse', 'Lakehouse Silver',   'ws-s',  25, 90),
      item('df',   'fabric_dataflow',  'Dataflow Transform', 'ws-s',  25, 220),
      item('nb',   'fabric_notebook',  'Notebook ETL',       'ws-s', 155, 220),

      cap('cap-b', 'Capacity Analytics (F32)', 'F32', 640, 0, 540, 440),
      ws('ws-g', 'Workspace Gold', 'cap-b',  30, 65, 220, 335),
      item('lh-g', 'fabric_lakehouse',      'Lakehouse Gold', 'ws-g', 25, 90),
      item('sm1',  'fabric_semantic_model', 'Semantic Model', 'ws-g', 25, 220),
      ws('ws-r', 'Workspace Reporting', 'cap-b', 290, 65, 220, 335),
      item('sm2',  'fabric_semantic_model', 'Semantic Model Exec',  'ws-r', 25, 90),
      item('rpt',  'fabric_report',         'Power BI Report',      'ws-r', 25, 220),
    ],
    edges: [
      edge('e1', 'pl',  'lh-b'),
      edge('e2', 'es',  'lh-b'),
      edge('e3', 'lh-b','lh-s'),
      edge('e4', 'df',  'lh-s'),
      edge('e5', 'lh-s','lh-g'),
      edge('e6', 'lh-g','sm1'),
      edge('e7', 'sm1', 'sm2'),
      edge('e8', 'sm2', 'rpt'),
    ],
  },

  {
    id: 'fabric-dataops',
    name: 'DataOps + Deployment Pipeline',
    description: '2 capacités Dev F8 / Prod F16 avec deployment pipeline pour promouvoir les artefacts Dev → Test → Prod. Marque de maturité des équipes data.',
    level: 'Production',
    icon: '🔄',
    nodes: [
      cap('cap-d', 'Capacity Dev (F8)', 'F8', 0, 0, 400, 420),
      ws('ws-d', 'Workspace Dev', 'cap-d', 30, 65, 340, 315),
      item('lh-d',  'fabric_lakehouse',          'Lakehouse Dev', 'ws-d',  30, 90),
      item('nb-d',  'fabric_notebook',            'Notebook Dev',  'ws-d', 200, 90),
      item('pl-d',  'fabric_pipeline',            'Pipeline Dev',  'ws-d',  30, 220),
      item('dp',    'fabric_deployment_pipeline', 'Deployment Pipeline', 'ws-d', 200, 220),

      cap('cap-p', 'Capacity Production (F16)', 'F16', 460, 0, 560, 420),
      ws('ws-t', 'Workspace Test', 'cap-p',  30, 65, 230, 315),
      item('lh-t', 'fabric_lakehouse', 'Lakehouse Test', 'ws-t', 25, 90),
      item('pl-t', 'fabric_pipeline',  'Pipeline Test',  'ws-t', 25, 220),
      ws('ws-p', 'Workspace Production', 'cap-p', 300, 65, 230, 315),
      item('lh-p',  'fabric_lakehouse',      'Lakehouse Prod',  'ws-p', 25,  90),
      item('sm-p',  'fabric_semantic_model', 'Semantic Model',  'ws-p', 25, 220),
      item('rpt-p', 'fabric_report',         'Power BI Report', 'ws-p', 155, 220),
    ],
    edges: [
      edge('e1', 'pl-d', 'lh-d'),
      edge('e2', 'dp',   'lh-t'),
      edge('e3', 'lh-t', 'lh-p'),
      edge('e4', 'lh-p', 'sm-p'),
      edge('e5', 'sm-p', 'rpt-p'),
    ],
  },

  // ── ENTERPRISE ────────────────────────────────────────────────────────────

  {
    id: 'fabric-enterprise',
    name: 'Enterprise Platform',
    description: '3 capacités dédiées : Data F32 (Bronze+Silver), Analytics F64 (Gold+Reporting), AI & DataScience F16. Isolation complète des workloads pour grands comptes.',
    level: 'Enterprise',
    icon: '🏢',
    nodes: [
      // Capacity Data
      cap('cap-data', 'Capacity Data (F32)', 'F32', 0, 0, 600, 450),
      ws('ws-b', 'Workspace Bronze', 'cap-data',  30, 65, 250, 340),
      item('lh-b', 'fabric_lakehouse',  'Lakehouse Bronze',   'ws-b',  30, 90),
      item('pl',   'fabric_pipeline',   'Pipeline Ingestion', 'ws-b',  30, 220),
      item('es',   'fabric_eventstream','EventStream',        'ws-b', 165, 90),
      ws('ws-s', 'Workspace Silver', 'cap-data', 320, 65, 250, 340),
      item('lh-s', 'fabric_lakehouse', 'Lakehouse Silver',   'ws-s',  30, 90),
      item('df',   'fabric_dataflow',  'Dataflow Transform', 'ws-s',  30, 220),
      item('nb',   'fabric_notebook',  'Notebook ETL',       'ws-s', 165, 220),

      // Capacity Analytics
      cap('cap-ana', 'Capacity Analytics (F64)', 'F64', 660, 0, 560, 450),
      ws('ws-g', 'Workspace Gold', 'cap-ana',  30, 65, 230, 340),
      item('lh-g', 'fabric_lakehouse',      'Lakehouse Gold', 'ws-g', 25, 90),
      item('sm1',  'fabric_semantic_model', 'Semantic Model', 'ws-g', 25, 220),
      ws('ws-r', 'Workspace Reporting', 'cap-ana', 300, 65, 230, 340),
      item('sm2',  'fabric_semantic_model', 'Semantic Model Exec', 'ws-r', 25, 90),
      item('rpt',  'fabric_report',         'Power BI Reports',   'ws-r', 25, 220),

      // Capacity AI
      cap('cap-ai', 'Capacity AI & DataScience (F16)', 'F16', 0, 510, 600, 420),
      ws('ws-ds', 'Workspace DataScience', 'cap-ai',  30, 65, 250, 310),
      item('lh-ds','fabric_lakehouse',   'Feature Store',      'ws-ds',  30, 90),
      item('nb-ml','fabric_notebook',    'Notebook ML',        'ws-ds',  30, 220),
      item('ml',   'fabric_ml_model',    'ML Model',           'ws-ds', 165, 220),
      ws('ws-ai', 'Workspace AI & Copilot', 'cap-ai', 320, 65, 250, 310),
      item('da',   'fabric_data_agent', 'Data Agent (Copilot)','ws-ai',  30, 90),
      item('eh',   'fabric_eventhouse', 'Eventhouse',          'ws-ai',  30, 220),
    ],
    edges: [
      edge('e1',  'pl',   'lh-b'),
      edge('e2',  'es',   'lh-b'),
      edge('e3',  'lh-b', 'lh-s'),
      edge('e4',  'df',   'lh-s'),
      edge('e5',  'lh-s', 'lh-g'),
      edge('e6',  'lh-g', 'sm1'),
      edge('e7',  'sm1',  'sm2'),
      edge('e8',  'sm2',  'rpt'),
      edge('e9',  'lh-g', 'lh-ds'),
      edge('e10', 'lh-ds','nb-ml'),
      edge('e11', 'nb-ml','ml'),
      edge('e12', 'lh-ds','da'),
    ],
  },

  // ── MULTI-DOMAIN ──────────────────────────────────────────────────────────

  {
    id: 'fabric-multidomain-bi',
    name: 'Multi-Domain BI',
    description: '1 capacity F32 · 3 domaines métier (Finance, Ventes, IT) chacun avec workspace + lakehouse + semantic model + rapport. Organisation par domaine pour gouvernance et sécurité.',
    level: 'Production',
    icon: '🏢',
    nodes: [
      cap('cap', 'Fabric Capacity (F32)', 'F32', 0, 0, 1230, 530),

      dom('dom-fin', 'Domaine Finance', 'cap', 20, 60, 370, 430),
      wsd('ws-fin', 'Workspace Finance', 'dom-fin', 'cap', 15, 55, 340, 345),
      item('lh-fin',  'fabric_lakehouse',      'Lakehouse Finance',   'ws-fin',  20, 90),
      item('sm-fin',  'fabric_semantic_model', 'Semantic Model Fin',  'ws-fin', 175, 90),
      item('rpt-fin', 'fabric_report',         'Rapport Finance',     'ws-fin',  20, 220),

      dom('dom-vte', 'Domaine Ventes', 'cap', 420, 60, 370, 430),
      wsd('ws-vte', 'Workspace Ventes', 'dom-vte', 'cap', 15, 55, 340, 345),
      item('lh-vte',  'fabric_lakehouse',      'Lakehouse Ventes',   'ws-vte',  20, 90),
      item('sm-vte',  'fabric_semantic_model', 'Semantic Model Vte', 'ws-vte', 175, 90),
      item('rpt-vte', 'fabric_report',         'Rapport Ventes',     'ws-vte',  20, 220),

      dom('dom-it', 'Domaine IT', 'cap', 820, 60, 390, 430),
      wsd('ws-it', 'Workspace Ingestion', 'dom-it', 'cap', 15, 55, 360, 345),
      item('pl-it',  'fabric_pipeline',  'Pipeline Ingestion',  'ws-it',  20, 90),
      item('lh-it',  'fabric_lakehouse', 'Lakehouse Bronze',    'ws-it', 185, 90),
      item('df-it',  'fabric_dataflow',  'Dataflow Transform',  'ws-it',  20, 220),
    ],
    edges: [
      edge('e1', 'lh-it',  'lh-fin'),
      edge('e2', 'lh-fin', 'sm-fin'),
      edge('e3', 'sm-fin', 'rpt-fin'),
      edge('e4', 'lh-it',  'lh-vte'),
      edge('e5', 'lh-vte', 'sm-vte'),
      edge('e6', 'sm-vte', 'rpt-vte'),
      edge('e7', 'pl-it',  'lh-it'),
      edge('e8', 'df-it',  'lh-it'),
    ],
  },

  {
    id: 'fabric-multidomain-enterprise',
    name: 'Enterprise Multi-Domain',
    description: '1 capacity F64 · 4 domaines (Finance, Commercial, RH, IT) en 2 rangées. Architecture de gouvernance enterprise avec isolation des données par domaine et pipeline centralisé.',
    level: 'Enterprise',
    icon: '🏛️',
    nodes: [
      cap('cap', 'Fabric Capacity (F64)', 'F64', 0, 0, 1280, 1070),

      dom('dom-fin', 'Domaine Finance', 'cap', 20, 60, 600, 460),
      wsd('ws-fin', 'Workspace Finance', 'dom-fin', 'cap', 15, 55, 570, 375),
      item('pl-fin',  'fabric_pipeline',      'Pipeline Finance',    'ws-fin',  20,  90),
      item('lh-fin',  'fabric_lakehouse',      'Lakehouse Finance',  'ws-fin', 195,  90),
      item('sm-fin',  'fabric_semantic_model', 'Semantic Model Fin', 'ws-fin',  20, 220),
      item('rpt-fin', 'fabric_report',         'Rapport Finance',    'ws-fin', 195, 220),

      dom('dom-com', 'Domaine Commercial', 'cap', 650, 60, 610, 460),
      wsd('ws-com', 'Workspace Commercial', 'dom-com', 'cap', 15, 55, 580, 375),
      item('pl-com',  'fabric_pipeline',      'Pipeline CRM',         'ws-com',  20,  90),
      item('lh-com',  'fabric_lakehouse',      'Lakehouse Commercial', 'ws-com', 195,  90),
      item('sm-com',  'fabric_semantic_model', 'Semantic Model Com',   'ws-com',  20, 220),
      item('rpt-com', 'fabric_report',         'Rapport Ventes',       'ws-com', 195, 220),

      dom('dom-rh', 'Domaine RH', 'cap', 20, 560, 600, 460),
      wsd('ws-rh', 'Workspace RH', 'dom-rh', 'cap', 15, 55, 570, 375),
      item('pl-rh',  'fabric_pipeline',  'Pipeline RH',      'ws-rh',  20,  90),
      item('lh-rh',  'fabric_lakehouse', 'Lakehouse RH',     'ws-rh', 195,  90),
      item('nb-rh',  'fabric_notebook',  'Notebook Analyse', 'ws-rh',  20, 220),
      item('rpt-rh', 'fabric_report',    'Rapport RH',       'ws-rh', 195, 220),

      dom('dom-it', 'Domaine IT / Data', 'cap', 650, 560, 610, 460),
      wsd('ws-brz', 'Workspace Bronze', 'dom-it', 'cap', 15, 55, 275, 375),
      item('pl-brz', 'fabric_pipeline',  'Pipeline Ingestion', 'ws-brz',  20, 90),
      item('lh-brz', 'fabric_lakehouse', 'Lakehouse Bronze',   'ws-brz',  20, 220),
      wsd('ws-gld', 'Workspace Gold', 'dom-it', 'cap', 310, 55, 285, 375),
      item('df-gld', 'fabric_dataflow',  'Dataflow Silver→Gold', 'ws-gld', 20,  90),
      item('lh-gld', 'fabric_lakehouse', 'Lakehouse Gold',       'ws-gld', 20, 220),
    ],
    edges: [
      edge('e1', 'pl-brz', 'lh-brz'),
      edge('e2', 'lh-brz', 'df-gld'),
      edge('e3', 'df-gld', 'lh-gld'),
      edge('e4', 'lh-brz', 'lh-fin'),
      edge('e5', 'lh-brz', 'lh-com'),
      edge('e6', 'lh-brz', 'lh-rh'),
      edge('e7',  'lh-fin', 'sm-fin'),
      edge('e8',  'sm-fin', 'rpt-fin'),
      edge('e9',  'lh-com', 'sm-com'),
      edge('e10', 'sm-com', 'rpt-com'),
      edge('e11', 'lh-rh',  'nb-rh'),
      edge('e12', 'nb-rh',  'rpt-rh'),
    ],
  },
];
